import { trackProductAction } from '@/libs/product-analytics';
import type { ApiResponse, CreateInvitationResponse } from '@fluenceur/types';
import { useMutation } from '@tanstack/react-query';
import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import { isPlanLimitError } from '@/libs/plan-limit-refusal';

type Input = {
  creatorId: string;
  programId: string;
  recommendationId?: string;
  /**
   * Brand-known email for the creator. When present it is stored on the
   * invitation so the EMAIL outreach path can reach the creator.
   */
  recipientEmail?: string;
};

const createProgramInvitationFn = async (params: Input): Promise<CreateInvitationResponse> => {
  const response = await api.post<ApiResponse<CreateInvitationResponse>>(
    `/dashboard/creators/${params.creatorId}/invitations`,
    {
      programId: params.programId,
      ...(params.recommendationId ? { recommendationId: params.recommendationId } : {}),
      ...(params.recipientEmail ? { recipientEmail: params.recipientEmail } : {}),
    }
  );

  if (response.data.success && response.data.data) {
    return response.data.data;
  }

  throw new Error(response.data.message || 'Failed to create program invitation');
};

export const useCreateProgramInvitation = () => {
  return useMutation<CreateInvitationResponse, Error, Input>({
    mutationFn: (input) =>
      trackProductAction('invitations', 'create', () => createProgramInvitationFn(input)),
    onError: (error) => {
      // A plan refusal opens the upsell from the API client; a toast would repeat it.
      if (isPlanLimitError(error)) return;
      toaster.error({
        title: 'Failed to create program invitation',
        description: error.message,
      });
    },
  });
};
